import React, { useCallback, useMemo, useState } from "react";
import { FS, NETWORK } from "src/config/constants";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import PoolFetcher from "src/scoring-fetcher/pool-monitoring/pool-fetcher";
import PoolPromiseFetcher from "src/scoring-fetcher/pool-monitoring/pool-promise-fetcher";
import { CHAIN_ALIASES } from "src/wallet-connection/chains";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";
import { useConfigContext } from "../config/configContext";
import { multiPoolClient } from "src/services/requestClient";
import { getChainAssetData } from "src/config/tokens";
import { ethers } from "ethers";
import useAuthorizationClient from "src/hooks/useAuthorizationClient";
import { SCORING_BASE_ROOT_URL } from "src/config/constants";
import LendingPoolConfiguratorABI from "src/assets/abis/poolMonitoring/LendingPoolConfigurator.json";
import { getNetworkTokens } from "src/config/tokens";

export const STATUS = {
  EXECUTED: { value: "EXECUTION", text: "Executed" },
  ACCEPTED: { value: "CONFIRMTRANSACTION", text: "Accepted" },
  PENDING: { value: "SUBMITTRANSACTION", text: "Pending" },
  CANCEL: { value: "REVOKECONFIRMATION", text: "Cancel" },
  FAILURE: { value: "EXECUTIONFAILURE", text: "Failure" },
};

const PoolMonitoringContext = React.createContext({
  data: {},
  fetchingDataStatus: FS.INITIAL,
  fetchPoolData: () => {},
});

const PoolMonitoringProvider = ({ children }) => {
  const { chain, address } = useWeb3Connect();
  const { currentProvider } = useProviderContext();
  const { addresses } = useConfigContext();

  const [data, setData] = useState({});
  const [fetchingDataStatus, setFetchingDataStatus] = useState(FS.INITIAL);
  const client = useAuthorizationClient(`${SCORING_BASE_ROOT_URL}/multi-pools`);

  const chainTokens = useMemo(() => {
    if (chain.chainId === CHAIN_ALIASES.BSC_MAINNET) return getNetworkTokens(NETWORK.BSC_MAINNET);
    else if (chain.chainId === CHAIN_ALIASES.BSC_TESTNET) return getNetworkTokens(NETWORK.BSC_TESTNET);
  }, [chain.chainId]);

  const fetchPoolData = useCallback(async () => {
    if (address) {
      setFetchingDataStatus(FS.FETCHING);
      const res = await multiPoolClient.get(
        `/pool-owner?accountAddress=${address}&chainId=${ethers.utils.hexlify(chain.chainId)}`
      );
      if (client) {
        let requests;
        const iface = new ethers.utils.Interface(LendingPoolConfiguratorABI);
        const poolListKeys = Object.keys(res);
        const result = {};
        for (const _key of poolListKeys) {
          if (_key !== "tokenList") {
            const _data = res[_key];
            requests = await client.get(
              `/update-events?chainId=${ethers.utils.hexlify(chain?.chainId)}&poolAddress=${
                _data["address"]
              }&accountAddress=${address}`
            );
            result[_key] = {
              overview: {
                name: _data["name"],
                owner: _data["creator"],
                poolAddress: _data["address"],
                providerId: _data["providerId"],
              },
              tokens: Object.keys(_data.tokens).map((tokenAddress, _) => {
                const tokenData = getChainAssetData(tokenAddress, chain.chainId);
                const apiData = _data.tokens[tokenAddress];
                return {
                  address: tokenAddress,
                  symbol: tokenData["symbol"],
                  totalSupply: apiData.supplyInUSD,
                  totalBorrow: apiData.borrowInUSD,
                  baseBorrowRate: apiData.borrowRate,
                  price: apiData.price,
                  risk: {
                    isActive: apiData.isActive,
                    isFreezed: apiData.isFreezed,
                    maxLTV: apiData.loanToValue,
                    liqThres: apiData.liquidationThreshold,
                    liqBonus: apiData.liquidationBonus,
                    reserveFactor: apiData.reserveFactor,
                    enableBorrow: apiData.enableBorrow,
                  },
                  dTokenEPS: apiData.espBorrower < 0 ? undefined : apiData.espBorrower,
                  tTokenEPS: apiData.espDepositor < 0 ? undefined : apiData.espDepositor,
                  utilizationOptimal: apiData.uOptimal,
                  slope1: apiData.slop1,
                  slope2: apiData.slop2,
                  image: tokenData["image"],
                };
              }),
              requests: requests?.map((request) => {
                const functionName = iface.getFunction("0x" + request["data"].slice(0, 8)).name;
                const fdata = iface.decodeFunctionData(functionName, "0x" + request["data"]);
                return {
                  ...request,
                  status:
                    STATUS[Object.keys(STATUS).find((status) => STATUS[status].value === request["event_type"])].text,
                  data: {
                    symbol: chainTokens[fdata["asset"]?.toLowerCase()]?.symbol,
                    ...fdata,
                  },
                  name: functionName,
                };
              }),
            };
          }
        }
        setData(result);
        setFetchingDataStatus(FS.SUCCESS);
      }
    }
  }, [client, addresses, chain.chainId, currentProvider, address]);

  const fetchContractPoolData = useCallback(async () => {
    if (addresses != {} && currentProvider) {
      setFetchingDataStatus(FS.FETCHING);
      let poolFetcher = undefined;
      if (chain.chainId == CHAIN_ALIASES.BSC_MAINNET) {
        console.debug("use muticall");
        poolFetcher = new PoolFetcher(addresses, currentProvider, NETWORK.BSC_MAINNET);
      } else if (chain.chainId == CHAIN_ALIASES.BSC_TESTNET) {
        console.debug("use promise");
        poolFetcher = new PoolPromiseFetcher(addresses, currentProvider, NETWORK.BSC_TESTNET);
      }
      const data = await poolFetcher.getPoolsData(address);
      setData(data);
      setFetchingDataStatus(FS.SUCCESS);
    }
  }, [addresses, chain.chainId, currentProvider]);

  const contextData = useMemo(
    () => ({ data, fetchingDataStatus, fetchPoolData, fetchContractPoolData, chainTokens }),
    [data, fetchingDataStatus, fetchPoolData, address]
  );

  return <PoolMonitoringContext.Provider value={contextData}>{children}</PoolMonitoringContext.Provider>;
};

export default PoolMonitoringProvider;

export const usePoolMonitoringContext = () => React.useContext(PoolMonitoringContext);
